import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { sanitize } from "../../lib/sanitize";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { RichTextEditor } from "../ui/RichTextEditor";
import { ArrowLeft, ArrowRight, Plus, Trash, Check, Buildings, Package, FileText, Eye, Image, Percent, UploadSimple, FilePdf } from "@phosphor-icons/react";
import { AttachmentGrid, isPdf } from "../ui/AttachmentGrid";
import ProductSearchInput from "../products/ProductSearchInput";

const STEPS = ["Cliente", "Items", "Terminos", "Adjuntos", "Vista Previa"];

const mediaUrl = (path) => {
  if (!path) return "";
  const clean = path.replace(/^quotations\//, "");
  return `${API}/quotations/media/${clean}`;
};

export default function QuotationCreate({ token, onNavigate, editId, prefillData }) {
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ company: "", name: "", email: "", phone: "" });
  const [showNewClient, setShowNewClient] = useState(false);
  const [items, setItems] = useState([{ product_name: "", description: "", quantity: 1, unit_price_usd: 0, unit_price_mxn: 0, discount_percent: 0 }]);
  const [exchangeRate, setExchangeRate] = useState(17.5);
  const [validityDays, setValidityDays] = useState(15);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [globalDiscountPercent, setGlobalDiscountPercent] = useState(0);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [config, setConfig] = useState({});
  const [termsTemplateIds, setTermsTemplateIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedBannerIds, setSelectedBannerIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [isProject, setIsProject] = useState(false);
  const [negociacionId, setNegociacionId] = useState("");
  const [negociaciones, setNegociaciones] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotations/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error("Error fetching clients:", e); }
  }, [token]);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quotations/config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(data || {});
      if (data?.default_validity_days && !editId) setValidityDays(data.default_validity_days);
      if (data?.default_payment_terms && !editId) setPaymentTerms(data.default_payment_terms);
      if (data?.default_notes && !editId) setNotes(data.default_notes);
      if (data?.tax_configs && !editId) {
        const defaults = (data.tax_configs || []).filter(t => t.is_default);
        setSelectedTaxes(defaults.map(t => ({ tax_id: t.id, name: t.name, rate: t.rate })));
      }
      // Auto-select first 3 T&C templates for new quotations
      if (data?.terms_templates && !editId) {
        const templates = data.terms_templates || [];
        const defaultIds = templates.slice(0, 3).map(t => t.id).filter(Boolean);
        setTermsTemplateIds(defaultIds);
      }
      // Auto-select all banners for new quotations
      if (data?.banner_images && !editId) {
        setSelectedBannerIds((data.banner_images || []).map((b, i) => b.id || `banner-${i}`));
      }
      // Auto-load default attachments for new quotations
      if (data?.default_attachments && !editId) {
        setAttachments((data.default_attachments || []).map(a => ({ ...a, is_default: true })));
      }
    } catch { /* ignore */ }
  }, [token, editId]);

  useEffect(() => {
    fetchClients();
    fetchConfig();
    if (editId) loadQuotation();
    // Fetch negociaciones for project linking
    axios.get(`${API}/negociaciones`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setNegociaciones(r.data)).catch(() => {});
  }, [fetchClients, fetchConfig, editId]);

  // Auto-create or select client from CRM prefill data
  useEffect(() => {
    if (!prefillData || prefillApplied || editId || clients.length === 0) return;
    const apply = async () => {
      const existing = clients.find(c =>
        c.company?.toLowerCase() === prefillData.company_name?.toLowerCase() &&
        c.name?.toLowerCase() === prefillData.contact_name?.toLowerCase()
      );
      if (existing) {
        setSelectedClient(existing);
        toast.success(`Cliente "${existing.company}" seleccionado`);
      } else {
        try {
          const clientData = {
            company: prefillData.company_name || "",
            name: prefillData.contact_name || "",
            email: prefillData.email || "",
            phone: prefillData.phone || prefillData.whatsapp || "",
          };
          const { data } = await axios.post(`${API}/quotations/clients`, clientData, { headers: { Authorization: `Bearer ${token}` } });
          setClients(prev => [...prev, data]);
          setSelectedClient(data);
          toast.success(`Cliente "${data.company}" creado y seleccionado`);
        } catch {
          toast.error("No se pudo crear el cliente desde el contacto comercial");
        }
      }
      setPrefillApplied(true);
    };
    apply();
  }, [prefillData, prefillApplied, editId, clients, token]);

  const loadQuotation = async () => {
    try {
      const { data } = await axios.get(`${API}/quotations/${editId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedClient({ id: data.client_id, name: data.client_name, email: data.client_email, company: data.client_company, phone: data.client_phone });
      const loadedItems = (data.items || []).map(i => ({
        product_name: i.product_name || i.description || "",
        description: i.description || "",
        quantity: i.quantity || 1,
        unit_price_usd: i.unit_price_usd || 0,
        unit_price_mxn: i.unit_price_mxn || 0,
        discount_percent: i.discount_percent || 0,
      }));
      setItems(loadedItems.length ? loadedItems : [{ product_name: "", description: "", quantity: 1, unit_price_usd: 0, unit_price_mxn: 0, discount_percent: 0 }]);
      setExchangeRate(data.exchange_rate || 17.5);
      setValidityDays(data.validity_days || 15);
      setPaymentTerms(data.payment_terms || "");
      setNotes(data.notes || "");
      setGlobalDiscountPercent(data.global_discount_percent || 0);
      setSelectedTaxes(data.taxes_detail || []);
      setTermsTemplateIds(data.terms_template_ids || (data.terms_template_id ? [data.terms_template_id] : []));
      setAttachments(data.attachments || []);
      setSelectedBannerIds(data.selected_banner_ids || []);
    } catch { toast.error("Error al cargar cotizacion"); }
  };

  const handleCreateClient = async () => {
    if (!newClient.company.trim()) { toast.error("Nombre de empresa requerido"); return; }
    if (!newClient.name.trim()) { toast.error("Nombre de encargado requerido"); return; }
    try {
      const { data } = await axios.post(`${API}/quotations/clients`, newClient, { headers: { Authorization: `Bearer ${token}` } });
      setClients(prev => [...prev, data]);
      setSelectedClient(data);
      setShowNewClient(false);
      setNewClient({ company: "", name: "", email: "", phone: "" });
      toast.success("Cliente creado");
    } catch { toast.error("Error al crear cliente"); }
  };

  const addItem = () => setItems(prev => [...prev, { product_name: "", description: "", quantity: 1, unit_price_usd: 0, unit_price_mxn: 0, discount_percent: 0 }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item };
      if (field === "product_name" || field === "description") {
        updated[field] = val;
      } else {
        updated[field] = parseFloat(val) || 0;
      }
      if (field === "unit_price_usd") {
        updated.unit_price_mxn = Math.round(updated.unit_price_usd * exchangeRate * 100) / 100;
      }
      return updated;
    }));
  };

  const recalcMxnAll = (newRate) => {
    setItems(prev => prev.map(item => ({
      ...item,
      unit_price_mxn: Math.round(item.unit_price_usd * newRate * 100) / 100,
    })));
  };

  const getItemSubtotal = (item) => ({
    mxn: Math.round(item.quantity * item.unit_price_mxn * 100) / 100,
    usd: Math.round(item.quantity * item.unit_price_usd * 100) / 100,
  });
  const getItemDiscount = (item) => {
    const sub = getItemSubtotal(item);
    return {
      mxn: Math.round(sub.mxn * item.discount_percent / 100 * 100) / 100,
      usd: Math.round(sub.usd * item.discount_percent / 100 * 100) / 100,
    };
  };
  const getItemTotal = (item) => {
    const sub = getItemSubtotal(item);
    const disc = getItemDiscount(item);
    return { mxn: Math.round((sub.mxn - disc.mxn) * 100) / 100, usd: Math.round((sub.usd - disc.usd) * 100) / 100 };
  };

  const subtotalMxn = items.reduce((s, i) => s + getItemTotal(i).mxn, 0);
  const subtotalUsd = items.reduce((s, i) => s + getItemTotal(i).usd, 0);
  const globalDiscMxn = Math.round(subtotalMxn * globalDiscountPercent / 100 * 100) / 100;
  const globalDiscUsd = Math.round(subtotalUsd * globalDiscountPercent / 100 * 100) / 100;
  const taxableMxn = subtotalMxn - globalDiscMxn;
  const taxableusd = subtotalUsd - globalDiscUsd;
  const taxesTotalMxn = selectedTaxes.reduce((s, t) => s + Math.round(taxableMxn * t.rate / 100 * 100) / 100, 0);
  const taxesTotalUsd = selectedTaxes.reduce((s, t) => s + Math.round(taxableusd * t.rate / 100 * 100) / 100, 0);
  const totalMxn = Math.round((taxableMxn + taxesTotalMxn) * 100) / 100;
  const totalUsd = Math.round((taxableusd + taxesTotalUsd) * 100) / 100;

  const canNext = () => {
    if (step === 0) return !!selectedClient;
    if (step === 1) return items.length > 0 && items.every(i => i.product_name.trim());
    return true;
  };

  const handleUploadAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = ["image/", "application/pdf"];
    const maxSize = 10 * 1024 * 1024;
    const valid = files.filter(f => {
      if (!allowed.some(t => f.type.startsWith(t))) { toast.error(`${f.name}: tipo no permitido (solo imagenes y PDFs)`); return false; }
      if (f.size > maxSize) { toast.error(`${f.name}: maximo 10MB`); return false; }
      return true;
    });
    if (!valid.length) return;
    setUploading(true);
    try {
      for (const file of valid) {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await axios.post(`${API}/quotations/attachments/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        setAttachments(prev => [...prev, { path: data.path, filename: data.filename, content_type: data.content_type }]);
      }
      toast.success(valid.length > 1 ? `${valid.length} archivos adjuntados` : "Archivo adjuntado");
    } catch { toast.error("Error al subir archivo"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeAttachment = (idx) => {
    if (attachments[idx]?.is_default) return; // Cannot remove default attachments
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };
  const updateAttachmentLabel = (idx, label) => {
    if (attachments[idx]?.is_default) return; // Cannot edit default labels
    setAttachments(prev => prev.map((a, i) => i === idx ? { ...a, label } : a));
  };

  const handleSave = async (status = "borrador") => {
    setSaving(true);
    try {
      const payload = {
        client_id: selectedClient.id,
        items: items.map(i => ({ product_name: i.product_name, description: i.description || null, quantity: i.quantity, unit_price_usd: i.unit_price_usd, unit_price_mxn: i.unit_price_mxn || null, discount_percent: i.discount_percent })),
        exchange_rate: exchangeRate,
        validity_days: validityDays,
        payment_terms: paymentTerms || null,
        notes: notes || null,
        status,
        global_discount_percent: globalDiscountPercent,
        taxes: selectedTaxes.map(t => ({ tax_id: t.tax_id, name: t.name, rate: t.rate })),
        terms_template_id: termsTemplateIds[0] || null,
        terms_template_ids: termsTemplateIds,
        selected_banner_ids: selectedBannerIds,
        attachments: attachments,
        is_project: isProject,
        negociacion_id: negociacionId || null,
      };
      if (editId) {
        await axios.put(`${API}/quotations/${editId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cotizacion actualizada");
      } else {
        await axios.post(`${API}/quotations`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(status === "pendiente" ? "Cotizacion creada y enviada" : "Cotizacion guardada como borrador");
      }
      onNavigate("list");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const filteredClients = clients.filter(c =>
    (c.company || "").toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.name || "").toLowerCase().includes(clientSearch.toLowerCase())
  );

  const taxConfigs = config.tax_configs || [];
  const termsTemplates = config.terms_templates || [];

  const toggleTax = (tc) => {
    setSelectedTaxes(prev => {
      const exists = prev.find(t => t.tax_id === tc.id);
      if (exists) return prev.filter(t => t.tax_id !== tc.id);
      return [...prev, { tax_id: tc.id, name: tc.name, rate: tc.rate }];
    });
  };

  const toggleTemplate = (templateId) => {
    setTermsTemplateIds(prev => {
      if (prev.includes(templateId)) return prev.filter(id => id !== templateId);
      return [...prev, templateId];
    });
  };

  const fmtMXN = (v) => (v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtUSD = (v) => (v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="quotation-create">
      <div className="flex items-center gap-3">
        <button onClick={() => onNavigate("list")} className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft size={20} /></button>
        <h2 className="text-xl font-bold text-slate-800">{editId ? "Editar" : "Crear"} Presupuesto</h2>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-1 shrink-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${i <= step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-400"}`}>{i + 1}</div>
            <span className={`text-xs hidden sm:inline ${i <= step ? "text-blue-600 font-medium" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? "bg-blue-600" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {/* Step 0: Client */}
      {step === 0 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2"><Buildings size={20} className="text-blue-600" /><h3 className="font-semibold text-slate-700">Seleccionar Cliente</h3></div>
          {selectedClient ? (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-5">
              <div>
                <p className="text-lg font-bold text-blue-800" data-testid="selected-client-company">{selectedClient.company || "Sin empresa"}</p>
                <p className="text-sm text-slate-600 mt-0.5">Encargado: <b>{selectedClient.name}</b></p>
                <div className="flex gap-4 mt-1 text-xs text-slate-500">
                  {selectedClient.email && <span>{selectedClient.email}</span>}
                  {selectedClient.phone && <span>{selectedClient.phone}</span>}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedClient(null)}>Cambiar</Button>
            </div>
          ) : (
            <>
              <input type="text" placeholder="Buscar por empresa o encargado..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-200 outline-none" data-testid="client-search" />
              <div className="max-h-52 overflow-y-auto space-y-1">
                {filteredClients.map(c => (
                  <button key={c.id} onClick={() => setSelectedClient(c)} className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors" data-testid={`select-client-${c.id}`}>
                    <span className="font-bold text-slate-800">{c.company || "Sin empresa"}</span>
                    <span className="text-slate-500 text-sm ml-2">- {c.name}</span>
                    {c.email && <span className="text-slate-400 text-xs ml-2">{c.email}</span>}
                  </button>
                ))}
                {filteredClients.length === 0 && <p className="text-center text-slate-400 text-sm py-3">No se encontraron clientes</p>}
              </div>
              <Button variant="outline" onClick={() => setShowNewClient(!showNewClient)} className="text-blue-600"><Plus size={14} className="mr-1" /> Nuevo Cliente</Button>
              {showNewClient && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div>
                    <label className="text-sm font-semibold text-slate-700">Empresa *</label>
                    <input placeholder="Nombre de la empresa" value={newClient.company} onChange={e => setNewClient(p => ({ ...p, company: e.target.value }))}
                      className="w-full px-3 py-2.5 border-2 border-blue-200 rounded-lg text-base font-semibold mt-1 focus:ring-2 focus:ring-blue-300 outline-none" data-testid="new-client-company" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Encargado *</label>
                      <input placeholder="Nombre del encargado" value={newClient.name} onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" data-testid="new-client-name" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Email</label>
                      <input placeholder="correo@empresa.com" value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Telefono</label>
                      <input placeholder="55 1234 5678" value={newClient.phone} onChange={e => setNewClient(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" />
                    </div>
                  </div>
                  <Button size="sm" onClick={handleCreateClient} className="bg-blue-600 w-full sm:w-auto">Crear Cliente</Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 1: Items */}
      {step === 1 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2"><Package size={20} className="text-blue-600" /><h3 className="font-semibold text-slate-700">Productos / Servicios</h3></div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 whitespace-nowrap">T.C. USD-MXN:</label>
              <input type="number" step="0.01" value={exchangeRate} onChange={e => { const v = parseFloat(e.target.value) || 0; setExchangeRate(v); recalcMxnAll(v); }}
                className="w-20 px-2 py-1 border rounded text-sm text-right" data-testid="exchange-rate" />
            </div>
          </div>

          <div className="space-y-4">
            {items.map((item, idx) => (
              <div key={idx} className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">ITEM {idx + 1}</span>
                  <button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600" disabled={items.length === 1}><Trash size={16} /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Producto / Servicio *</label>
                    <ProductSearchInput
                      token={token}
                      value={item.product_name}
                      placeholder="Buscar producto o escribir manualmente..."
                      onManualChange={(v) => updateItem(idx, "product_name", v)}
                      onSelect={(product) => {
                        setItems(prev => prev.map((it, i) => i === idx ? {
                          ...it,
                          product_name: product.name,
                          description: product.description || "",
                          unit_price_usd: product.precio_lista || 0,
                          unit_price_mxn: 0,
                          product_id: product.id,
                          product_sku: product.sku,
                          product_stock: product.stock_qty
                        } : it));
                      }}
                    />
                    {item.product_sku && (
                      <div className="flex items-center gap-2 mt-1 text-[10px]">
                        <span className="font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{item.product_sku}</span>
                        <span className={item.product_stock > 0 ? "text-emerald-600 font-bold" : "text-red-500 font-bold"}>
                          {item.product_stock > 0 ? `${item.product_stock} en stock` : "Sin stock"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Descripcion adicional</label>
                    <input placeholder="Detalles, modelo, especificaciones..." value={item.description} onChange={e => updateItem(idx, "description", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1" data-testid={`item-desc-${idx}`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Cantidad</label>
                    <input type="number" min="1" step="1" value={item.quantity} onChange={e => updateItem(idx, "quantity", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1 text-center" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">P.U. USD</label>
                    <input type="number" step="0.01" placeholder="0.00" value={item.unit_price_usd || ""} onChange={e => updateItem(idx, "unit_price_usd", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1 text-right" data-testid={`item-usd-${idx}`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">P.U. MXN <span className="text-blue-500">(auto)</span></label>
                    <input type="number" step="0.01" placeholder="0.00" value={item.unit_price_mxn || ""} onChange={e => updateItem(idx, "unit_price_mxn", e.target.value.toString())}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1 text-right bg-blue-50/50" data-testid={`item-mxn-${idx}`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Descuento %</label>
                    <input type="number" min="0" max="100" step="1" value={item.discount_percent || ""} onChange={e => updateItem(idx, "discount_percent", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1 text-center" data-testid={`item-disc-${idx}`} />
                  </div>
                  <div className="flex flex-col justify-end">
                    <label className="text-xs font-medium text-slate-500">Total</label>
                    <div className="mt-1 px-3 py-2 bg-white border rounded-lg">
                      <p className="text-sm font-bold text-blue-700">${fmtMXN(getItemTotal(item).mxn)}</p>
                      <p className="text-xs text-slate-400">${fmtUSD(getItemTotal(item).usd)} USD</p>
                    </div>
                  </div>
                </div>
                {item.discount_percent > 0 && (
                  <p className="text-xs text-orange-600">Descuento aplicado: -${fmtMXN(getItemDiscount(item).mxn)} MXN / -${fmtUSD(getItemDiscount(item).usd)} USD</p>
                )}
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addItem}><Plus size={14} className="mr-1" /> Agregar Item</Button>

          {/* Global discount + taxes */}
          <div className="border-t pt-4 space-y-3">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="text-xs font-medium text-slate-500 flex items-center gap-1"><Percent size={12} /> Descuento Global %</label>
                <input type="number" min="0" max="100" step="1" value={globalDiscountPercent || ""} onChange={e => setGlobalDiscountPercent(parseFloat(e.target.value) || 0)}
                  className="w-24 px-3 py-2 border rounded-lg text-sm mt-1 text-center" data-testid="global-discount" />
              </div>
              {taxConfigs.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Impuestos</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {taxConfigs.map(tc => {
                      const isSelected = selectedTaxes.some(t => t.tax_id === tc.id);
                      return (
                        <button key={tc.id} onClick={() => toggleTax(tc)} data-testid={`tax-toggle-${tc.id}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${isSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"}`}>
                          {tc.name} ({tc.rate}%)
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Totals summary */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Subtotal</span>
              <span><b className="text-blue-700">${fmtMXN(subtotalMxn)}</b> <span className="text-slate-400 ml-2">${fmtUSD(subtotalUsd)} USD</span></span>
            </div>
            {globalDiscountPercent > 0 && (
              <div className="flex justify-between text-sm text-orange-600">
                <span>Descuento global ({globalDiscountPercent}%)</span>
                <span>-${fmtMXN(globalDiscMxn)} MXN / -${fmtUSD(globalDiscUsd)} USD</span>
              </div>
            )}
            {selectedTaxes.map(t => (
              <div key={t.tax_id} className="flex justify-between text-sm text-slate-600">
                <span>{t.name} ({t.rate}%)</span>
                <span>${fmtMXN(Math.round(taxableMxn * t.rate / 100 * 100) / 100)} MXN</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t border-slate-300">
              <span className="font-bold text-slate-700">TOTAL</span>
              <span><b className="text-xl text-blue-700">${fmtMXN(totalMxn)}</b> <span className="text-green-700 ml-2 font-semibold">${fmtUSD(totalUsd)} USD</span></span>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Terms */}
      {step === 2 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2"><FileText size={20} className="text-blue-600" /><h3 className="font-semibold text-slate-700">Terminos y Condiciones</h3></div>

          {termsTemplates.length > 0 && (
            <div>
              <label className="text-sm font-medium text-slate-600">Plantillas de T&C <span className="text-slate-400 font-normal">(selecciona multiples)</span></label>
              <div className="flex flex-wrap gap-2 mt-2">
                {termsTemplates.map(tmpl => {
                  const isSelected = termsTemplateIds.includes(tmpl.id);
                  return (
                    <button key={tmpl.id} onClick={() => toggleTemplate(tmpl.id)} data-testid={`template-${tmpl.id}`}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${isSelected ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"}`}>
                      <span className={`w-4 h-4 rounded flex items-center justify-center border ${isSelected ? "bg-white border-white" : "border-slate-400"}`}>
                        {isSelected && <Check size={10} className="text-blue-600" weight="bold" />}
                      </span>
                      <span className="flex items-center gap-1.5">
                        {tmpl.font_color && <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tmpl.font_color }}></span>}
                        {tmpl.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {termsTemplateIds.length > 0 && (
                <p className="text-xs text-blue-600 mt-2">{termsTemplateIds.length} plantilla(s) seleccionada(s) - se incluiran en el PDF</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-600">Validez (dias)</label>
              <input type="number" value={validityDays} onChange={e => setValidityDays(parseInt(e.target.value) || 15)} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" data-testid="validity-days" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm font-medium text-slate-600">Forma de Pago</label>
              {config.default_payment_terms && <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">Pre-llenado - editable</span>}
            </div>
            <RichTextEditor value={paymentTerms} onChange={setPaymentTerms} placeholder="Ej: 50% al inicio, 50% contra entrega." compact testId="payment-terms" minHeight="100px" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm font-medium text-slate-600">Notas / Terminos adicionales</label>
              {config.default_notes && <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">Pre-llenado - editable</span>}
            </div>
            <RichTextEditor value={notes} onChange={setNotes} placeholder="Condiciones especiales, garantias, etc..." testId="quotation-notes" minHeight="120px" />
          </div>

          {/* Project / Negociacion link */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-3 mb-3">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="is-project-toggle">
                <input type="checkbox" checked={isProject} onChange={e => { setIsProject(e.target.checked); if (!e.target.checked) setNegociacionId(""); }}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm font-medium text-slate-700">Es proyecto</span>
              </label>
            </div>
            {isProject && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <label className="text-xs font-semibold text-blue-700">Vincular a Negociacion</label>
                <select value={negociacionId} onChange={e => setNegociacionId(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white" data-testid="neg-link-select">
                  <option value="">Sin vincular</option>
                  {negociaciones.map(n => (
                    <option key={n.id} value={n.id}>{n.number} — {n.company_name || n.title} (${(n.amount || 0).toLocaleString("es-MX")})</option>
                  ))}
                </select>
                <p className="text-[10px] text-blue-600">Al vincular, el monto de la cotizacion se sincronizara con la negociacion</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Attachments + Banners */}
      {step === 3 && (
        <div className="bg-white rounded-xl border p-6 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2"><Image size={20} className="text-blue-600" /><h3 className="font-semibold text-slate-700">Archivos Adjuntos</h3></div>

            {/* Default attachments from config (read-only) */}
            {attachments.filter(a => a.is_default).length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-400 mb-2 uppercase">Predeterminados (desde configuracion)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {attachments.filter(a => a.is_default).map((att, i) => {
                    const pdf = isPdf(att);
                    const url = `${API}/quotations/attachments/${(att.path || "").replace("quotations/attachments/", "")}`;
                    return (
                      <div key={`def-${i}`} className="relative border rounded-lg overflow-hidden bg-white opacity-90" data-testid={`default-att-preview-${i}`}>
                        {pdf ? (
                          <div className="w-full h-28 flex flex-col items-center justify-center bg-red-50 gap-1 cursor-pointer" onClick={() => window.open(url, "_blank")}>
                            <FilePdf size={30} className="text-red-500" weight="duotone" />
                            {att.label && <span className="text-[10px] font-bold text-red-800 bg-red-100 px-1.5 py-0.5 rounded">{att.label}</span>}
                          </div>
                        ) : (
                          <img src={url} alt="" className="w-full h-28 object-cover" />
                        )}
                        <p className="text-[10px] text-slate-400 px-2 py-1 truncate border-t bg-slate-50">{att.filename || "Adjunto"}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upload extra files */}
            <p className="text-sm text-slate-500 mb-1">Subir archivos adicionales (imagenes, catalogos PDF)</p>
            <label className={`flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${uploading ? "bg-blue-50 border-blue-300" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/30"}`}>
              <input type="file" accept="image/*,.pdf,application/pdf" multiple onChange={handleUploadAttachment} className="hidden" disabled={uploading} data-testid="attachment-upload" />
              {uploading ? (
                <span className="text-sm text-blue-600 font-medium">Subiendo archivos...</span>
              ) : (
                <>
                  <UploadSimple size={20} className="text-blue-500" />
                  <span className="text-sm text-slate-600">Agregar imagenes o PDFs adicionales (max 10MB c/u)</span>
                </>
              )}
            </label>

            {/* Extra (non-default) attachments - editable */}
            {attachments.filter(a => !a.is_default).length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-400 mb-2 uppercase">Archivos adicionales</p>
                <AttachmentGrid
                  attachments={attachments.filter(a => !a.is_default)}
                  apiBase={API}
                  onRemove={(idx) => {
                    const extraStart = attachments.findIndex(a => !a.is_default);
                    removeAttachment(extraStart + idx);
                  }}
                  onUpdateLabel={(idx, label) => {
                    const extraStart = attachments.findIndex(a => !a.is_default);
                    updateAttachmentLabel(extraStart + idx, label);
                  }}
                  editable
                />
              </div>
            )}
          </div>

          {/* Banners from Config */}
          {(() => {
            const banners = config.banner_images || [];
            if (banners.length === 0) return null;
            const POSITION_LABELS = { before_items: "Antes de productos", after_totals: "Despues de totales", before_managers: "Antes de gerentes", end: "Al final" };
            return (
              <div className="pt-4 border-t" data-testid="banner-selection-section">
                <h4 className="text-sm font-semibold text-slate-600 mb-2">Banners Promocionales</h4>
                <p className="text-xs text-slate-400 mb-3">Selecciona los banners configurados que deseas incluir en este presupuesto.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {banners.map((b, idx) => {
                    const bid = b.id || `banner-${idx}`;
                    const isSelected = selectedBannerIds.includes(bid);
                    return (
                      <div key={bid}
                        onClick={() => setSelectedBannerIds(prev => isSelected ? prev.filter(id => id !== bid) : [...prev, bid])}
                        className={`cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${isSelected ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200 opacity-60 hover:opacity-80"}`}
                        data-testid={`banner-select-${idx}`}>
                        <img src={mediaUrl(b.path)} alt={b.filename || `Banner ${idx + 1}`}
                          className="w-full h-20 object-cover" />
                        <div className="flex items-center justify-between px-2 py-1.5 bg-slate-50">
                          <span className="text-xs text-slate-500">{POSITION_LABELS[b.position || "end"] || b.position}</span>
                          <span className={`w-5 h-5 rounded flex items-center justify-center border ${isSelected ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>
                            {isSelected && <Check size={12} className="text-white" weight="bold" />}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedBannerIds.length > 0 && (
                  <p className="text-xs text-blue-600 mt-2">{selectedBannerIds.length} banner(s) seleccionado(s)</p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Step 4: Preview */}
      {step === 4 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2"><Eye size={20} className="text-blue-600" /><h3 className="font-semibold text-slate-700">Vista Previa</h3></div>
          <div className="border rounded-lg p-5 bg-slate-50">
            <div className="flex justify-between mb-4">
              <div>
                <p className="font-bold text-lg text-blue-800">{selectedClient?.company || "Sin empresa"}</p>
                <p className="text-sm text-slate-600">Encargado: <b>{selectedClient?.name}</b></p>
                {selectedClient?.email && <p className="text-xs text-slate-400">{selectedClient.email}</p>}
                {selectedClient?.phone && <p className="text-xs text-slate-400">{selectedClient.phone}</p>}
              </div>
              <div className="text-right text-sm text-slate-500">
                <p>Validez: {validityDays} dias</p>
                <p>T.C.: ${exchangeRate} MXN/USD</p>
              </div>
            </div>

            <table className="w-full text-sm border-collapse mb-3">
              <thead><tr className="bg-blue-600 text-white">
                <th className="px-2 py-2 text-left rounded-tl-lg">Producto</th>
                <th className="px-2 py-2 text-center">Cant.</th>
                <th className="px-2 py-2 text-right">P.U. USD</th>
                <th className="px-2 py-2 text-right">P.U. MXN</th>
                <th className="px-2 py-2 text-center">Desc.%</th>
                <th className="px-2 py-2 text-right">Total MXN</th>
                <th className="px-2 py-2 text-right rounded-tr-lg">Total USD</th>
              </tr></thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="px-2 py-2">
                      <span className="font-medium">{item.product_name}</span>
                      {item.description && <span className="text-xs text-slate-400 block">{item.description}</span>}
                    </td>
                    <td className="px-2 py-2 text-center">{item.quantity}</td>
                    <td className="px-2 py-2 text-right">${fmtUSD(item.unit_price_usd)}</td>
                    <td className="px-2 py-2 text-right">${fmtMXN(item.unit_price_mxn)}</td>
                    <td className="px-2 py-2 text-center">{item.discount_percent > 0 ? `${item.discount_percent}%` : "-"}</td>
                    <td className="px-2 py-2 text-right font-medium text-blue-700">${fmtMXN(getItemTotal(item).mxn)}</td>
                    <td className="px-2 py-2 text-right font-medium text-green-700">${fmtUSD(getItemTotal(item).usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="space-y-1 text-sm text-right">
              <div className="flex justify-end gap-6"><span className="text-slate-500">Subtotal:</span><span className="font-semibold">${fmtMXN(subtotalMxn)} MXN</span><span className="text-slate-400">${fmtUSD(subtotalUsd)} USD</span></div>
              {globalDiscountPercent > 0 && (
                <div className="flex justify-end gap-6 text-orange-600"><span>Descuento ({globalDiscountPercent}%):</span><span>-${fmtMXN(globalDiscMxn)}</span></div>
              )}
              {selectedTaxes.map(t => (
                <div key={t.tax_id} className="flex justify-end gap-6 text-slate-600"><span>{t.name} ({t.rate}%):</span><span>${fmtMXN(Math.round(taxableMxn * t.rate / 100 * 100) / 100)}</span></div>
              ))}
              <div className="flex justify-end gap-6 pt-2 border-t font-bold text-base"><span>TOTAL:</span><span className="text-blue-700">${fmtMXN(totalMxn)} MXN</span><span className="text-green-700">${fmtUSD(totalUsd)} USD</span></div>
            </div>

            {paymentTerms && paymentTerms !== "<p><br></p>" && (
              <div className="text-sm mt-3">
                <b>Forma de pago:</b>
                <div className="mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={sanitize(paymentTerms)} />
              </div>
            )}
            {notes && notes !== "<p><br></p>" && (
              <div className="text-sm mt-1">
                <b>Notas:</b>
                <div className="mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={sanitize(notes)} />
              </div>
            )}

            {/* T&C Templates Preview */}
            {termsTemplateIds.length > 0 && (() => {
              const selected = termsTemplates.filter(t => termsTemplateIds.includes(t.id));
              const POSITION_LABELS = { before_items: "Antes de los productos", after_totals: "Despues de totales", before_managers: "Antes de los gerentes", end: "Al final del documento" };
              if (selected.length === 0) return null;
              return (
                <div className="mt-4 pt-4 border-t space-y-3" data-testid="tc-preview-section">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Terminos y Condiciones ({selected.length})</p>
                  {selected.map(tmpl => (
                    <div key={tmpl.id} className="rounded-lg border overflow-hidden" data-testid={`tc-preview-${tmpl.id}`}>
                      <div className="px-3 py-2 text-xs font-semibold text-white" style={{ backgroundColor: tmpl.font_color || "#1E293B" }}>
                        {tmpl.name}
                        <span className="ml-2 opacity-75 font-normal">({POSITION_LABELS[tmpl.position || "after_totals"] || tmpl.position})</span>
                      </div>
                      <div className="px-4 py-3 bg-slate-50/80 prose prose-sm max-w-none" style={{
                        color: tmpl.font_color || "#1E293B",
                        fontFamily: tmpl.font_family || "Helvetica, sans-serif",
                        fontSize: `${tmpl.font_size || 9}px`,
                        lineHeight: 1.5,
                      }} dangerouslySetInnerHTML={sanitize(tmpl.content || "")} />
                    </div>
                  ))}
                </div>
              );
            })()}

            {attachments.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-semibold text-slate-500 mb-2">ARCHIVOS ADJUNTOS ({attachments.length})</p>
                <div className="flex gap-2 flex-wrap">
                  {attachments.map((att, i) => (
                    isPdf(att) ? (
                      <div key={i} className="w-24 h-20 rounded-lg border bg-red-50 flex flex-col items-center justify-center gap-0.5">
                        <FilePdf size={20} className="text-red-500" />
                        <span className="text-[9px] font-bold text-red-800 truncate max-w-[88px] px-1">{att.label || "PDF"}</span>
                        <span className="text-[8px] text-red-500 truncate max-w-[88px] px-1">{att.filename || ""}</span>
                      </div>
                    ) : (
                      <img key={i} src={`${API}/quotations/attachments/${(att.path || att).toString().replace("quotations/attachments/", "")}`} alt=""
                        className="w-20 h-20 object-cover rounded-lg border" />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => step > 0 ? setStep(step - 1) : onNavigate("list")} disabled={saving}>
          <ArrowLeft size={16} className="mr-1" /> {step === 0 ? "Cancelar" : "Anterior"}
        </Button>
        <div className="flex gap-2">
          {step === STEPS.length - 1 && (
            <>
              <Button variant="outline" onClick={() => handleSave("borrador")} disabled={saving} data-testid="save-draft">
                Guardar Borrador
              </Button>
              <Button onClick={() => handleSave("pendiente")} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-and-send">
                <Check size={16} className="mr-1" /> {saving ? "Guardando..." : "Guardar y Enviar"}
              </Button>
            </>
          )}
          {step < STEPS.length - 1 && (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="bg-blue-600 hover:bg-blue-700" data-testid="next-step">
              Siguiente <ArrowRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
